import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { tenantApi } from '../lib/tenant-api';
import { getErrorMessage } from '../lib/api-client';
import { useTenantBrandingFromUrl } from '../hooks/useTenantBrandingFromUrl';

export default function ForgotPassword() {
  const { tenantName, canonicalSubdomain } = useTenantBrandingFromUrl();
  const [email, setEmail] = useState('');
  const [tenantSubdomain, setTenantSubdomain] = useState('');
  const [showSubdomainField, setShowSubdomainField] = useState(true);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (canonicalSubdomain) {
      setShowSubdomainField(false);
      setTenantSubdomain(canonicalSubdomain);
    }
  }, [canonicalSubdomain]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await tenantApi.requestPasswordReset({
        email,
        tenant_subdomain: tenantSubdomain,
      });
      setSubmitted(true);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            {tenantName ? `Reset password — ${tenantName}` : 'Reset your password'}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Enter your email and we will send you a reset link (expires in 1 hour).
          </p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:px-10">
          {submitted ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-700">
                If an account exists with that email, you will receive a password reset link shortly.
                Check your inbox and spam folder.
              </p>
              <Link
                to="/login"
                className="block text-center text-sm font-medium text-indigo-600 hover:text-indigo-500"
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit}>
              {error && (
                <div className="bg-red-50 p-4">
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
              )}

              {showSubdomainField && (
                <div>
                  <label htmlFor="tenant_subdomain" className="block text-sm font-medium text-gray-700">
                    Organization Subdomain
                  </label>
                  <div className="mt-1">
                    <input
                      id="tenant_subdomain"
                      name="tenant_subdomain"
                      type="text"
                      required
                      value={tenantSubdomain}
                      onChange={(e) => setTenantSubdomain(e.target.value)}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="your-organization"
                    />
                  </div>
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Sending...' : 'Send reset link'}
              </button>

              <p className="text-center text-sm">
                <Link to="/login" className="font-medium text-indigo-600 hover:text-indigo-500">
                  Back to sign in
                </Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
